'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { studioClient } from '@/api/client/studio.client';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { captureError } from '@/shared/lib/errors/capture';
import { useUploadStore, type ActiveUpload } from '@/shared/stores/upload-store';
import { describeProcessingFailure } from '@/shared/utils/bunny-failure';
import {
  BUNNY_FAILED_STATUSES,
  BUNNY_NOT_READY_STATUSES,
  BUNNY_PROCESSING_STATUSES,
  BUNNY_UPLOADING_STATUSES,
} from '@/types/studio';
import {
  createRefreshEscalation,
  isQuietLongEnough,
  type RefreshEscalation,
} from '../lib/refresh-escalation';
import { createRefreshQueue, REFRESH_PRIORITY, type RefreshCandidate } from '../lib/refresh-queue';

/**
 * GET backoff ladder. Reads our own column, so it is cheap — the cap stays
 * tight rather than saving requests we do not need to save. Measured encodes
 * include 41s, 84s, 99s and 154s, so a 60s cap would surface a 41-second short
 * about 34 seconds late. Any real movement resets to the first rung.
 */
const POLL_DELAYS_MS = [5_000, 10_000, 20_000, 30_000] as const;
const MAX_CONSECUTIVE_FAILURES = 5;
/** While the API is unreachable, poll every Nth tick instead of every tick. */
const POLL_LOST_TICK_DIVISOR = 3;

/**
 * How long `bunny_status` may sit at 0/absent after the last byte landed before
 * we call it stalled.
 *
 * Sized against the BE reaper's ceiling, not against raw observation. BACK-209
 * shipped a reconciler that closes the same gap every 5 min, and this client
 * now escalates to `POST .../bunny-status/refresh` after a minute of silence.
 * With both paths active, three minutes of no movement means Bunny itself is
 * stuck — not that we were slow to look. (Was 8 min while the refresh escape
 * hatch was unwired and the reaper was the only backstop.)
 */
const INGEST_STALL_MS = 3 * 60 * 1000;

/**
 * How long the column may sit at status >= 1 without finishing. Deliberately
 * generous: a 26 GB upload legitimately took 59 min. The event carries
 * `total_bytes` so analysis can normalise rather than relying on this constant
 * to be right for every file size.
 *
 * Unchanged by BACK-209 on purpose. Terminal reconciliation already existed and
 * only got faster (15 -> 5 min cadence), so anything still unfinished at 30 min
 * now means Bunny is genuinely slow rather than that we failed to notice.
 */
const ENCODE_STALL_MS = 30 * 60 * 1000;

interface BunnyVideoFields {
  bunny_status?: number;
  bunny_encode_progress?: number;
  bunny_last_error?: string | null;
  /**
   * Provenance of the current `bunny_status`, added by BACK-209.
   *
   * Both can be null here, and the reason is the deploy window, not the video
   * type: the migration is nullable with no backfill on purpose, so a row
   * written before BACK-209 shipped carries NULL until its next transition.
   * An upload already in flight across that deploy therefore polls with no
   * provenance for a while. (`VideoResource` also omits the pair entirely when
   * `is_bunny_video` is false, but that is legacy pre-2025 videos — nothing
   * this poller can ever see, since every row it watches was just created by
   * `PrepareVideoBunnyUpload`, which sets `is_bunny_video => true`.)
   *
   * `'reconciler'` is the interesting value: the BE reaper had to pull the
   * status from Bunny's live API because the webhook had not arrived yet. That
   * turns a stall event from "something was slow" into "the webhook stream
   * lagged and the reaper covered for it", with no log dig.
   */
  bunny_status_source?: 'webhook' | 'reconciler' | 'initial_upload' | 'manual_refresh';
  bunny_status_updated_at?: string | null;
}

/** What a refresh attempt did, so a caller can react to throttling. */
export interface RefreshOutcome {
  throttled: boolean;
  syncedNow: boolean;
  retryAfterSeconds?: number;
}

/**
 * What the global refresh gate did to get this video its slot (FRONT-438).
 *
 * Only the automatic path has one: the manual button deliberately skips both
 * the escalation ladder and the 1/sec queue, so there is nothing to describe.
 */
interface RefreshGateContext {
  priority: 'focused' | 'background';
  /** Jitter sample drawn for the next refresh; null once exhausted. */
  nextDelayMs: number | null;
  suppressed: number;
  candidates: number;
}

type PollErrorKind = 'http_4xx' | 'http_5xx' | 'http_other' | 'network' | 'timeout' | 'unknown';

interface ClassifiedPollError {
  kind: PollErrorKind;
  httpStatus: number | null;
  message: string;
}

function classifyPollError(err: unknown): ClassifiedPollError {
  const e = err as { response?: { status?: number }; code?: string; message?: string };
  const rawMessage = typeof e.message === 'string' ? e.message : String(err);
  const message = rawMessage.slice(0, 200);
  const httpStatus = typeof e.response?.status === 'number' ? e.response.status : null;
  if (httpStatus !== null) {
    if (httpStatus >= 500) return { kind: 'http_5xx', httpStatus, message };
    if (httpStatus >= 400) return { kind: 'http_4xx', httpStatus, message };
    return { kind: 'http_other', httpStatus, message };
  }
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') {
    return { kind: 'timeout', httpStatus: null, message };
  }
  if (e.code === 'ERR_NETWORK' || e.code === 'ENOTFOUND' || e.code === 'ECONNREFUSED') {
    return { kind: 'network', httpStatus: null, message };
  }
  return { kind: 'unknown', httpStatus: null, message };
}

type UrlFetchTerminalEvent =
  typeof AnalyticsEvent.STUDIO_URL_FETCH_COMPLETED | typeof AnalyticsEvent.STUDIO_URL_FETCH_FAILED;

/**
 * Import-from-link funnel terminal (FRONT-341). No-op for TUS uploads — the
 * entry's `fetchClientId` is what marks it as a url-fetch. `elapsed_seconds`
 * runs from the store entry's creation, i.e. from the dispatch-200.
 */
function captureUrlFetchTerminal(
  event: UrlFetchTerminalEvent,
  upload: ActiveUpload,
  props: Record<string, unknown>
): void {
  if (!upload.fetchClientId) return;
  posthog.capture(event, {
    upload_id: upload.id,
    video_uuid: upload.videoUuid,
    bunny_video_id: upload.bunnyVideoId,
    client_id: upload.fetchClientId,
    elapsed_seconds: Math.round((Date.now() - upload.startedAt) / 1000),
    ...props,
  });
}

/**
 * Polls the API for uploads in 'processing' phase.
 *
 * Transitions:
 * - bunny_status NOT in BUNNY_NOT_READY_STATUSES → 'complete' (unknown future
 *   statuses count as ready — BE emits codes beyond the documented 0-10 range)
 * - bunny_status in BUNNY_FAILED_STATUSES (5, 8) → 'error'
 * - 5 consecutive poll failures → stays 'processing' with `pollLost: true`,
 *   backs off, keeps polling, clears on the next success or `online` event.
 *   Never 'error': Bunny keeps encoding while the client is blind (FRONT-355).
 *
 * Also tracks bunny_encode_progress for UI visibility.
 *
 * Mounted in GlobalUploadIndicator so it runs whenever there are
 * active uploads visible to the user.
 */
export function useProcessingPoller() {
  const queryClient = useQueryClient();
  const setPhase = useUploadStore((state) => state.setPhase);
  const updateUpload = useUploadStore((state) => state.updateUpload);
  const errorCountsRef = useRef<Map<string, number>>(new Map());
  const previousStatusRef = useRef<Map<string, number>>(new Map());
  const previousProgressRef = useRef<Map<string, number>>(new Map());
  // `${uploadId}:${stallKind}` for stalls already reported, so a 40-minute wait
  // emits one event per kind rather than one every 10s tick.
  const stallReportedRef = useRef<Set<string>>(new Set());
  /** Last time status or encode progress actually advanced, per upload. */
  const lastMovementAtRef = useRef<Map<string, number>>(new Map());
  const nextRefreshAtRef = useRef<Map<string, number>>(new Map());
  const escalationsRef = useRef<Map<string, RefreshEscalation>>(new Map());
  const refreshQueueRef = useRef(createRefreshQueue());
  /** Current rung of POLL_DELAYS_MS. Lives in a ref, not state: a re-render
   * per backoff step would be pointless churn, and the loop reads it directly. */
  const pollDelayIndexRef = useRef(0);
  const pollDelayRef = useRef<number>(POLL_DELAYS_MS[0]);

  /**
   * Identity of the processing set as a primitive.
   *
   * Subscribing to the whole `uploads` map re-rendered this hook on every store
   * write — roughly 20/s while TUS is streaming, since the indicator mounting
   * this poller is always alive. Worse, the polling effect depended on that
   * value, so it tore down and rebuilt its timer constantly: harmless with a
   * fixed interval, fatal for a backoff, which would reset before it could ever
   * escalate. A joined id string only changes when the set itself changes.
   */
  const processingKey = useUploadStore((state) => {
    const ids: string[] = [];
    for (const upload of state.uploads.values()) {
      if (upload.phase === 'processing' && upload.videoUuid && !upload.isStale) {
        ids.push(upload.id);
      }
    }
    return ids.sort().join(',');
  });

  const escalationFor = useCallback((uploadId: string): RefreshEscalation => {
    const existing = escalationsRef.current.get(uploadId);
    if (existing) return existing;
    const created = createRefreshEscalation();
    escalationsRef.current.set(uploadId, created);
    return created;
  }, []);

  /** Any real advance restarts both clocks: the GET ladder and the escalation. */
  const noteMovement = useCallback(
    (uploadId: string) => {
      lastMovementAtRef.current.set(uploadId, Date.now());
      nextRefreshAtRef.current.delete(uploadId);
      escalationFor(uploadId).onProgressObserved();
      pollDelayIndexRef.current = 0;
      pollDelayRef.current = POLL_DELAYS_MS[0];
    },
    [escalationFor]
  );

  /**
   * Ask the BE to read through to Bunny's live API. `trigger` separates the
   * automatic escalation from the creator pressing the button, because the two
   * have very different expectations about being throttled.
   */
  const runRefresh = useCallback(
    async (
      upload: ActiveUpload,
      trigger: 'auto' | 'manual',
      gate?: RefreshGateContext
    ): Promise<RefreshOutcome> => {
      if (!upload.videoUuid) return { throttled: false, syncedNow: false };
      // Both manual buttons land here, and the content row sets its own
      // `surface` at its own capture site. Naming this one explicitly means the
      // two are distinguished by a value rather than by one of them omitting
      // the property, which is not a contract anything can rely on.
      const surface = trigger === 'manual' ? 'global_indicator' : 'poller';
      try {
        const result = await studioClient.refreshBunnyStatus(upload.videoUuid);
        posthog.capture(AnalyticsEvent.STUDIO_UPLOAD_STATUS_REFRESHED, {
          stage: 'encoding',
          upload_id: upload.id,
          video_uuid: upload.videoUuid,
          bunny_video_id: upload.bunnyVideoId,
          trigger,
          surface,
          synced_now: result.synced_now,
          live_bunny_status: result.live_bunny_status,
          bunny_status: result.bunny_status,
          bunny_status_source: result.bunny_status_source,
          // How long the column has been sitting where it is. This is the field
          // that showed a live Bunny read still returning "Encoding" 30 minutes
          // in — i.e. genuinely slow, not a lost webhook — and it belongs on
          // every status-bearing event, not just the stall one.
          bunny_status_updated_at: result.bunny_status_updated_at,
          // Last progress the client saw. Observed as 0 in 100% of production
          // samples so far; captured here so it is a series rather than the
          // single point the stall event gave us, which cannot distinguish
          // "Bunny never sends it" from "we drop it".
          encode_progress: upload.encodeProgress ?? null,
          escalation_step: escalationFor(upload.id).snapshot().step,
          // Which rung of the GET ladder (POLL_DELAYS_MS) this video was on.
          // `escalation_step` covers the refresh ladder; this is the other one,
          // and without it "how long had we been asking?" was unanswerable.
          poll_rung: pollDelayIndexRef.current,
          poll_delay_ms: pollDelayRef.current,
          // Absent on manual refreshes: the button skips escalation and the
          // queue entirely, so there is no gate behaviour to report.
          ...(gate
            ? {
                refresh_priority: gate.priority,
                // The jitter sample actually drawn, not the ladder ceiling it
                // was drawn from. Full jitter means the two differ by a lot,
                // and the sample is what decides when Bunny is next asked.
                next_refresh_delay_ms: gate.nextDelayMs,
                // How hard the global 1/sec cap was biting. Attached to the
                // winner instead of firing per suppression, which on a 50-video
                // bulk run would be the same burst this queue exists to prevent.
                refresh_suppressed_count: gate.suppressed,
                refresh_candidates: gate.candidates,
              }
            : {}),
        });
        // A sync means the column was stale and is now current — that counts as
        // movement, so the ladders reset and the next GET picks up the change.
        if (result.synced_now) noteMovement(upload.id);
        return { throttled: false, syncedNow: result.synced_now };
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status ?? null;
        // 429 is the BE's 6/min per-video guard. Honour `retry_after` and do not
        // retry on our own — the normal GET loop keeps running regardless.
        if (status === 429) {
          const retryAfter =
            (err as { response?: { data?: { retry_after?: number } } })?.response?.data
              ?.retry_after ?? 60;
          nextRefreshAtRef.current.set(upload.id, Date.now() + retryAfter * 1000);
          posthog.capture(AnalyticsEvent.STUDIO_UPLOAD_STATUS_REFRESH_THROTTLED, {
            stage: 'encoding',
            upload_id: upload.id,
            video_uuid: upload.videoUuid,
            trigger,
            retry_after_seconds: retryAfter,
          });
          return { throttled: true, syncedNow: false, retryAfterSeconds: retryAfter };
        }
        // Everything else is non-fatal: the video is unaffected and the GET loop
        // is still the primary path, so this is a warning, not a failure.
        captureError(err instanceof Error ? err : new Error('Bunny status refresh failed'), {
          component: 'ProcessingPoller',
          action: 'status-refresh-failed',
          severity: 'warning',
          extra: { upload_id: upload.id, video_uuid: upload.videoUuid, trigger, status },
        });
        return { throttled: false, syncedNow: false };
      }
    },
    [escalationFor, noteMovement]
  );

  /**
   * Creator-initiated check. Deliberately skips the escalation clock and the
   * global queue: this is an explicit request from someone staring at a
   * spinner, it fits inside the BE's 6/min per-video budget, and making them
   * wait behind a background sweep would defeat the point of the button.
   */
  const refreshStatus = useCallback(
    async (uploadId: string): Promise<RefreshOutcome> => {
      const upload = useUploadStore.getState().uploads.get(uploadId);
      if (!upload) return { throttled: false, syncedNow: false };
      return runRefresh(upload, 'manual');
    },
    [runRefresh]
  );

  // Clean up per-upload bookkeeping for uploads that left 'processing' phase
  useEffect(() => {
    const processingIds = new Set(processingKey ? processingKey.split(',') : []);
    const forgetStale = (map: Map<string, unknown>) => {
      for (const id of map.keys()) {
        if (!processingIds.has(id)) map.delete(id);
      }
    };
    forgetStale(errorCountsRef.current);
    forgetStale(previousStatusRef.current);
    forgetStale(previousProgressRef.current);
    forgetStale(lastMovementAtRef.current);
    forgetStale(nextRefreshAtRef.current);
    forgetStale(escalationsRef.current);
    for (const key of stallReportedRef.current) {
      if (!processingIds.has(key.slice(0, key.lastIndexOf(':')))) {
        stallReportedRef.current.delete(key);
      }
    }
  }, [processingKey]);

  useEffect(() => {
    if (processingKey === '') return;

    const pollOne = async (upload: ActiveUpload) => {
      // Discriminates the ingestion path in the shared upload funnel so
      // url-fetch encode/ready/fail rates can be segmented from TUS. Declared
      // before the try so the poll-failure branch can tag it too.
      const source = upload.fetchClientId ? 'url_fetch' : 'tus';
      try {
        const video = await studioClient.getVideo(upload.videoUuid!);
        // The API returns bunny fields but the Video type doesn't include them
        const {
          bunny_status: bunnyStatus,
          bunny_encode_progress: encodeProgress,
          bunny_last_error: bunnyLastError,
          bunny_status_source: bunnyStatusSource,
          bunny_status_updated_at: bunnyStatusUpdatedAt,
        } = video as unknown as BunnyVideoFields;

        // Reset consecutive error count on successful poll
        errorCountsRef.current.delete(upload.id);
        if (upload.pollLost) updateUpload(upload.id, { pollLost: false });

        // Detect first transition into encoding (status >= 1, not yet ready/failed).
        // Fires once per upload when Bunny first acknowledges the file. Absence of
        // this event after STUDIO_UPLOAD_TUS_COMPLETED ⇒ Bunny never received the
        // file (orphan stub), distinct from "Bunny received it but never finished".
        const previousStatus = previousStatusRef.current.get(upload.id);
        const wasEncoding = previousStatus !== undefined && previousStatus >= 1;
        const isCurrentlyEncoding =
          bunnyStatus !== undefined &&
          bunnyStatus >= 1 &&
          (BUNNY_PROCESSING_STATUSES.has(bunnyStatus) || BUNNY_UPLOADING_STATUSES.has(bunnyStatus));
        // Wall-clock the creator has been staring at a "Processing" spinner.
        const processingSince = upload.processingStartedAt ?? upload.startedAt;
        const waitingMs = Math.max(0, Date.now() - processingSince);

        if (!wasEncoding && isCurrentlyEncoding) {
          posthog.capture(AnalyticsEvent.STUDIO_ENCODING_STARTED, {
            stage: 'encoding',
            upload_id: upload.id,
            video_uuid: upload.videoUuid,
            bunny_video_id: upload.bunnyVideoId,
            source,
            from_status: previousStatus ?? null,
            to_status: bunnyStatus,
            // Emitted directly rather than derived by joining this event to
            // studio_upload_tus_completed on video_uuid: that join silently
            // drops rows whenever either side is missing, which produced
            // nonsense gaps during the 2026-09-08 investigation.
            ack_gap_ms: waitingMs,
            total_bytes: upload.bytesTotal,
            // 'reconciler' here means Bunny's webhook had not landed and the BE
            // reaper pulled the status from their live API instead — the exact
            // shape behind the 2026-09-08 38-minute wait.
            bunny_status_source: bunnyStatusSource ?? null,
            bunny_status_updated_at: bunnyStatusUpdatedAt ?? null,
          });
        }
        // Movement = status advanced OR encode progress advanced. Status alone
        // is the wrong signal: a video legitimately holds status 2 for an entire
        // encode while progress climbs, and that is healthy, not stuck.
        const previousProgress = previousProgressRef.current.get(upload.id);
        const statusMoved = bunnyStatus !== undefined && bunnyStatus !== previousStatus;
        const progressMoved =
          encodeProgress !== undefined &&
          previousProgress !== undefined &&
          encodeProgress > previousProgress;
        const firstObservation = previousStatus === undefined;
        if (statusMoved || progressMoved || firstObservation) noteMovement(upload.id);

        if (bunnyStatus !== undefined) {
          previousStatusRef.current.set(upload.id, bunnyStatus);
        }
        if (encodeProgress !== undefined) {
          previousProgressRef.current.set(upload.id, encodeProgress);
        }

        // Track encode progress for UI
        if (encodeProgress !== undefined) {
          updateUpload(upload.id, { encodeProgress });
        }

        // Ready — processing complete. Blocklist check so unknown future
        // statuses (e.g. 13) finish instead of polling forever; failed
        // statuses are in the not-ready set and fall through to the branch below.
        if (bunnyStatus !== undefined && !BUNNY_NOT_READY_STATUSES.has(bunnyStatus)) {
          setPhase(upload.id, 'complete');
          posthog.capture(AnalyticsEvent.STUDIO_VIDEO_READY, {
            stage: 'ready',
            upload_id: upload.id,
            video_uuid: upload.videoUuid,
            bunny_video_id: upload.bunnyVideoId,
            source,
            bunny_status: bunnyStatus,
            // How long the creator actually waited. Self-contained: answering
            // "how long did this take" previously meant joining this event to
            // studio_upload_tus_completed on video_uuid, which silently drops
            // rows whenever either side is missing and produced values like
            // -1788435762 mid-investigation, twice.
            //
            // This is client-observed, so it is inflated when the tab was
            // closed for part of the wait — a cohort split on
            // studio_upload_abandoned put the closed-tab mean at 11660s against
            // 2755s for creators who stayed. Pair it with
            // bunny_status_updated_at below, which is the server's record of
            // when the status actually reached ready, to separate "Bunny was
            // slow" from "nobody was looking".
            processing_seconds: Math.round((Date.now() - processingSince) / 1000),
            bunny_status_source: bunnyStatusSource ?? null,
            bunny_status_updated_at: bunnyStatusUpdatedAt ?? null,
            encode_progress: encodeProgress ?? null,
          });
          captureUrlFetchTerminal(AnalyticsEvent.STUDIO_URL_FETCH_COMPLETED, upload, {
            bunny_status: bunnyStatus,
          });
          // Invalidate content list so thumbnail appears immediately
          queryClient.invalidateQueries({ queryKey: ['studio', 'videos'] });
          queryClient.invalidateQueries({ queryKey: ['studio', 'shorts'] });
          return;
        }

        // Still not ready. Report the two stall shapes once each, so a wait
        // that used to be invisible until a creator complained shows up as
        // data. This measures OUR view of Bunny — `bunny_status` is a BE column
        // fed by a Bunny webhook, so a stall can be a webhook that never
        // arrived, one processed late, or Bunny genuinely still working.
        const isFailed = bunnyStatus !== undefined && BUNNY_FAILED_STATUSES.has(bunnyStatus);
        if (!isFailed) {
          // Status 0 counts as NOT acknowledged on purpose, and this is the one
          // place a numeric comparison on bunny_status is correct. It does not
          // mean "Queued" here — it means the column never moved off its initial
          // value, the 2026-09-08 shape where bytes were delivered and the
          // webhook never landed. That case needs the 3-minute ingest threshold,
          // not the 30-minute encode one.
          //
          // 6/7 fall on the acknowledged side, which is also right:
          // PresignedUploadFinished means the bytes did land, and the BE reaper
          // needs up to 5 minutes to advance the row (BO d89eb02), so a
          // 3-minute alarm there would fire before the rescue arrives.
          const acknowledged = bunnyStatus !== undefined && bunnyStatus >= 1;
          const stallKind = acknowledged ? 'encode' : 'ingest';
          const threshold = acknowledged ? ENCODE_STALL_MS : INGEST_STALL_MS;
          const stallKey = `${upload.id}:${stallKind}`;

          if (waitingMs > threshold && !stallReportedRef.current.has(stallKey)) {
            stallReportedRef.current.add(stallKey);
            posthog.capture(AnalyticsEvent.STUDIO_UPLOAD_PROCESSING_STALLED, {
              stage: 'encoding',
              upload_id: upload.id,
              video_uuid: upload.videoUuid,
              bunny_video_id: upload.bunnyVideoId,
              source,
              stall_kind: stallKind,
              waiting_ms: waitingMs,
              threshold_ms: threshold,
              bunny_status: bunnyStatus ?? null,
              encode_progress: encodeProgress ?? null,
              total_bytes: upload.bytesTotal,
              content_type: upload.contentType,
              poll_lost: upload.pollLost === true,
              bunny_status_source: bunnyStatusSource ?? null,
              bunny_status_updated_at: bunnyStatusUpdatedAt ?? null,
            });
          }
        }

        // Failed — Bunny reports a terminal failure
        if (isFailed) {
          // Prefer the real Bunny encoder string when BE surfaced it
          // (PR A — `bunny_last_error`). Falls back to legacy generic copy
          // when null (pre-deploy ghost rows or webhook arrived empty).
          const errorMsg =
            bunnyLastError ?? 'Video processing failed on the server. Please try uploading again.';
          // Stable enum for dashboards/alerts — groups by cause, not by Bunny's prose.
          const failureReason = describeProcessingFailure(bunnyLastError).reason;

          updateUpload(upload.id, {
            phase: 'error',
            error: errorMsg,
            failureStage: 'processing',
          });

          // captureError (not bare Sentry) so the Bunny encoder string reaches
          // PostHog client_error — Sentry ingestion has been dead since 2026-07-16.
          captureError(new Error(errorMsg), {
            component: 'ProcessingPoller',
            action: 'bunny-processing-failed',
            tags: { flow: 'upload', step: 'processing-poller' },
            extra: {
              upload_id: upload.id,
              video_uuid: upload.videoUuid,
              bunny_video_id: upload.bunnyVideoId,
              bunny_status: bunnyStatus,
              bunny_last_error: bunnyLastError,
              failure_reason: failureReason,
              source,
            },
          });

          posthog.capture(AnalyticsEvent.STUDIO_UPLOAD_PROCESSING_FAILED, {
            stage: 'encoding',
            upload_id: upload.id,
            video_uuid: upload.videoUuid,
            bunny_video_id: upload.bunnyVideoId,
            source,
            bunny_status: bunnyStatus,
            failure_reason: failureReason,
          });
          captureUrlFetchTerminal(AnalyticsEvent.STUDIO_URL_FETCH_FAILED, upload, {
            step: 'encoding',
            error_code: `bunny_${bunnyStatus}`,
            error_message: errorMsg,
            http_status: null,
            bunny_status: bunnyStatus,
          });
        }
      } catch (err) {
        const count = (errorCountsRef.current.get(upload.id) ?? 0) + 1;
        errorCountsRef.current.set(upload.id, count);
        // Report the threshold crossing once; later failures in the same outage
        // keep counting silently so one loss is one event, not one per tick.
        if (count !== MAX_CONSECUTIVE_FAILURES) return;

        // Losing the poll is not losing the video. Writing `error` here made
        // transient outages render as "Processing failed [bunny_unknown]" and
        // stopped polling for good — reload was the only recovery.
        updateUpload(upload.id, { pollLost: true });

        captureError(err instanceof Error ? err : new Error('Processing poll lost'), {
          component: 'ProcessingPoller',
          action: 'poll-network-failed',
          tags: { flow: 'upload', step: 'processing-poller-network' },
          extra: {
            upload_id: upload.id,
            video_uuid: upload.videoUuid,
            bunny_video_id: upload.bunnyVideoId,
            consecutive_failures: count,
            source,
          },
        });

        // last_observed_bunny_status disambiguates orphan-stub root cause:
        // null ⇒ Bunny never acknowledged the file (likely Bunny/BE prepare gap),
        // 0/1/2 ⇒ Bunny had it but BE polling endpoint then started failing.
        const classified = classifyPollError(err);
        const lastObservedStatus = previousStatusRef.current.get(upload.id) ?? null;
        posthog.capture(AnalyticsEvent.STUDIO_UPLOAD_POLL_FAILED, {
          stage: 'encoding',
          upload_id: upload.id,
          video_uuid: upload.videoUuid,
          bunny_video_id: upload.bunnyVideoId,
          source,
          consecutive_failures: count,
          last_observed_bunny_status: lastObservedStatus,
          last_http_status: classified.httpStatus,
          last_error_kind: classified.kind,
          last_error_message: classified.message,
        });
      }
    };

    let tick = 0;
    let inFlight = false;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    /** Live processing set. Read per tick rather than closed over, so the loop
     * never restarts (and never resets its backoff) on an unrelated store write. */
    const currentProcessing = (): ActiveUpload[] =>
      Array.from(useUploadStore.getState().uploads.values()).filter(
        (u) => u.phase === 'processing' && u.videoUuid && !u.isStale
      );

    // `fetch` hangs until the OS timeout during an outage; without this guard
    // overlapping ticks would stack hung requests and inflate the failure count.
    const pollAll = async (force = false) => {
      if (inFlight) return;
      inFlight = true;
      tick += 1;
      try {
        const before = pollDelayIndexRef.current;
        for (const upload of currentProcessing()) {
          if (upload.pollLost && !force && tick % POLL_LOST_TICK_DIVISOR !== 0) continue;
          await pollOne(upload);
        }
        // noteMovement resets the ladder to rung 0. If the whole sweep left it
        // where it was, nothing moved anywhere — step down a rung.
        if (pollDelayIndexRef.current === before) {
          pollDelayIndexRef.current = Math.min(before + 1, POLL_DELAYS_MS.length - 1);
          pollDelayRef.current =
            POLL_DELAYS_MS[pollDelayIndexRef.current] ?? POLL_DELAYS_MS[POLL_DELAYS_MS.length - 1]!;
        }
        await maybeRefreshOne();
      } finally {
        inFlight = false;
      }
    };

    /**
     * The escape hatch. `GET` above reads a BE column fed by a Bunny webhook;
     * when that column stops moving, this reads through to Bunny's live API.
     * At most one video per tick, globally rate-gated — the per-video 6/min BE
     * limit does not bound a bulk upload, only this does.
     */
    const maybeRefreshOne = async () => {
      const now = Date.now();
      const candidates: RefreshCandidate[] = [];

      for (const upload of currentProcessing()) {
        if (upload.pollLost) continue; // API unreachable — refresh would fail too
        const lastMovement = lastMovementAtRef.current.get(upload.id) ?? now;
        if (!isQuietLongEnough(now - lastMovement)) continue;
        if (now < (nextRefreshAtRef.current.get(upload.id) ?? 0)) continue;

        const escalation = escalationFor(upload.id);
        if (escalation.isExhausted()) continue; // past reaper parity — cron wins

        candidates.push({
          uploadId: upload.id,
          // Attention, not fairness: in a bulk run the creator is watching one
          // video, and the rest are covered by the BE reaper anyway.
          priority: upload.hasOpenModal ? REFRESH_PRIORITY.focused : REFRESH_PRIORITY.background,
          eligibleSince: lastMovement,
        });
      }

      const winner = refreshQueueRef.current.take(candidates, now);
      if (!winner) return;

      const upload = currentProcessing().find((u) => u.id === winner.uploadId);
      if (!upload?.videoUuid) return;

      const escalation = escalationFor(upload.id);
      const justExhausted = escalation.onRefreshIssued();
      const nextDelay = escalation.nextDelayMs();
      nextRefreshAtRef.current.set(upload.id, now + (nextDelay ?? 0));

      const takeStats = refreshQueueRef.current.lastTakeStats();
      await runRefresh(upload, 'auto', {
        priority: winner.priority === REFRESH_PRIORITY.focused ? 'focused' : 'background',
        nextDelayMs: nextDelay,
        suppressed: takeStats.suppressed,
        candidates: takeStats.candidates,
      });

      // Record the hand-off to the cron. Fired after the refresh so the status
      // it reports is the one we gave up on, and once per stall episode per tab:
      // onRefreshIssued only returns true on the transition, but movement resets
      // the escalation and every tab runs its own poller.
      if (justExhausted) {
        posthog.capture(AnalyticsEvent.STUDIO_UPLOAD_REFRESH_EXHAUSTED, {
          stage: 'encoding',
          upload_id: upload.id,
          video_uuid: upload.videoUuid,
          bunny_video_id: upload.bunnyVideoId,
          escalation_step: escalation.snapshot().step,
          waiting_ms: now - (upload.processingStartedAt ?? upload.startedAt),
          encode_progress: upload.encodeProgress ?? null,
          // No bunny_status here on purpose: the store entry does not carry it,
          // and the studio_upload_status_refreshed fired microseconds earlier by
          // the same refresh already has it. Join on upload_id.
        });
      }
    };

    const onOnline = () => {
      void pollAll(true);
    };

    /** Self-scheduling rather than setInterval: a fixed interval cannot back
     * off, and rebuilding it per store change would reset the backoff forever. */
    const schedule = () => {
      if (stopped) return;
      timer = setTimeout(() => {
        void pollAll().finally(schedule);
      }, pollDelayRef.current);
    };
    schedule();
    window.addEventListener('online', onOnline);

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('online', onOnline);
    };
    // Every dependency here is render-stable — `processingKey` is a joined id
    // string and the rest are useCallback/zustand handles — so this effect only
    // re-runs when the processing set actually changes. That is load-bearing:
    // a dependency that changed per store write would rebuild the timer and
    // reset the backoff before it could ever escalate.
  }, [processingKey, setPhase, updateUpload, queryClient, escalationFor, noteMovement, runRefresh]);

  return { refreshStatus };
}
